import { NGROK } from "../../../APIs";
import { apiClient } from "../../../utils/apiClient";

const SECRET_DISCOVERY_BASE = `${NGROK}/api/secret-discovery`;

export type SecretDiscoveryScanState =
  | "IN_PROGRESS"
  | "COMPLETED"
  | "COMPLETED_WITH_ERRORS"
  | "FAILED"
  | "CANCELLED";

export type SecretDiscoveryTargetType =
  | "COMPUTE_VM"
  | "STORAGE_BUCKET"
  | "DATABASE"
  | "KUBERNETES_CLUSTER";

export interface SecretDiscoveryScan {
  scanId: string;
  tenantName: string;
  projectId: string;
  status: SecretDiscoveryScanState;
  running: boolean;
  initiatedBy?: string;
  startedAt?: string;
  endedAt?: string;
  durationMillis?: number;
  targetTypes?: SecretDiscoveryTargetType[];
  targetsPlanned: number;
  targetsCompleted: number;
  targetsFailed: number;
  targetsSkipped: number;
  secretsScanned: number;
  secretsSkipped: number;
  findingsCount: number;
  progressPercent: number;
  errorMessage?: string;
}

export interface SecretDiscoveryFailureReasonCount {
  reason: string;
  count: number;
}

export interface SecretDiscoveryScanTypeState {
  targetType: SecretDiscoveryTargetType;
  supported: boolean;
  everScanned: boolean;
  running: boolean;
  status?: SecretDiscoveryScanState;
  scanId?: string;
  initiatedBy?: string;
  startedAt?: string;
  endedAt?: string;
  durationMillis?: number;
  targetsPlanned: number;
  targetsCompleted: number;
  targetsFailed: number;
  targetsSkipped: number;
  findingsCount: number;
  progressPercent: number;
  errorMessage?: string;
  failureReasons: SecretDiscoveryFailureReasonCount[];
}

interface ApiFootprintLocation {
  findingId: string;
  resourceName: string;
  targetType: SecretDiscoveryTargetType;
  zone?: string;
  storedAs?: string;
  locationType: string;
  locationLabel?: string;
  locationPath?: string;
  lineNumber?: number;
  matchEncoding?: string;
  confidence?: number;
  contextRedacted?: string;
  status: string;
  firstSeenAt?: string;
  lastSeenAt?: string;
}

interface ApiFootprintResponse {
  secretName: string;
  secretDisplayName?: string;
  totalLocations: number;
  countsByTargetType: Record<string, number>;
  lastScannedAt?: string;
  lastScan?: SecretDiscoveryScan | null;
  locations: ApiFootprintLocation[];
}

export type SecretDiscoverySourceType =
  | "compute"
  | "storage"
  | "database"
  | "kubernetes"
  | "endpoint"
  | "github"
  | "gitlab";

export interface SecretDiscoveryLocation {
  id: string;
  sourceType: SecretDiscoverySourceType;
  sourceLabel: string;
  resourceName: string;
  storedAs: string;
  location: string;
  environment?: "Production" | "Staging" | "Development";
  region: string;
  branch?: string;
  lastSeenAt: string;

  locationType?: string;
  locationPath?: string;
  lineNumber?: number;
  confidence?: number;
  matchEncoding?: string;
  contextRedacted?: string;
  firstSeenAt?: string;
}

export interface SecretDiscoveryData {
  secretName: string;
  scanId: string | null;

  scanState: SecretDiscoveryScanState | null;

  scanStatus: string;
  scannedAt: string;
  locations: SecretDiscoveryLocation[];
}

export interface FetchSecretDiscoveryParams {
  cloudProvider: string;
  secretName: string;
  projectId?: string;
  tenantName?: string;
}

const TARGET_TYPE_TO_SOURCE: Record<
  SecretDiscoveryTargetType,
  { sourceType: SecretDiscoverySourceType; label: string }
> = {
  COMPUTE_VM: { sourceType: "compute", label: "Virtual machine" },
  STORAGE_BUCKET: { sourceType: "storage", label: "Storage bucket" },
  DATABASE: { sourceType: "database", label: "Database" },
  KUBERNETES_CLUSTER: { sourceType: "kubernetes", label: "Kubernetes cluster" },
};

export const SCAN_STATE_LABELS: Record<SecretDiscoveryScanState, string> = {
  IN_PROGRESS: "Scan in progress",
  COMPLETED: "Completed",
  COMPLETED_WITH_ERRORS: "Completed with errors",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatScanTimestamp(value?: string | null): string {
  if (!value) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day, hour, minute] = match;
  const monthLabel = MONTHS[Number(month) - 1] || month;
  return `${day} ${monthLabel} ${year}, ${hour}:${minute}`;
}

export function formatDuration(millis?: number | null): string {
  if (!millis || millis < 0) return "-";
  const totalSeconds = Math.round(millis / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export const getShortSecretName = (value: string): string => {
  const parts = String(value || "").split("/").filter(Boolean);
  return parts[parts.length - 1] || "";
};

export const SUPPORTED_SECRET_DISCOVERY_PROVIDERS = ["gcp", "aws", "azure"];

const SUPPORTED_PROVIDERS = new Set(SUPPORTED_SECRET_DISCOVERY_PROVIDERS);

function normalizeProvider(cloudProvider?: string): string {
  return String(cloudProvider || "").toLowerCase();
}

function isSupportedProvider(cloudProvider?: string): boolean {
  return SUPPORTED_PROVIDERS.has(normalizeProvider(cloudProvider));
}

function toLocation(location: ApiFootprintLocation): SecretDiscoveryLocation {
  const source = TARGET_TYPE_TO_SOURCE[location.targetType] || {
    sourceType: "compute" as SecretDiscoverySourceType,
    label: location.targetType,
  };
  return {
    id: location.findingId,
    sourceType: source.sourceType,
    sourceLabel: source.label,
    resourceName: location.resourceName,
    storedAs: location.storedAs || "-",
    location: location.locationLabel || location.locationPath || "-",
    region: location.zone || "-",
    locationType: location.locationType,
    lastSeenAt: formatScanTimestamp(location.lastSeenAt),
    locationPath: location.locationPath,
    lineNumber: location.lineNumber,
    confidence: location.confidence,
    matchEncoding: location.matchEncoding,
    contextRedacted: location.contextRedacted,
    firstSeenAt: formatScanTimestamp(location.firstSeenAt),
  };
}

export async function fetchSecretDiscovery({
  cloudProvider,
  secretName,
  projectId,
  tenantName,
}: FetchSecretDiscoveryParams): Promise<SecretDiscoveryData | null> {
  if (!isSupportedProvider(cloudProvider) || !secretName || !projectId || !tenantName) {
    return null;
  }

  const response = await apiClient<ApiFootprintResponse>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/footprint`,
    params: { tenantName, projectId, secretName },
  });

  if (!response || !Array.isArray(response.locations) || response.locations.length === 0) {
    return null;
  }

  const scanState = response.lastScan?.status ?? null;
  return {
    secretName: response.secretDisplayName || getShortSecretName(response.secretName),
    scanId: response.lastScan?.scanId ?? null,
    scanState,
    scanStatus: scanState ? SCAN_STATE_LABELS[scanState] : "Completed",
    scannedAt: formatScanTimestamp(response.lastScannedAt),
    locations: response.locations.map(toLocation),
  };
}

export async function fetchSecretFootprintCounts(
  cloudProvider: string,
  tenantName?: string,
  projectId?: string,
): Promise<Record<string, number>> {
  if (!isSupportedProvider(cloudProvider) || !tenantName || !projectId) {
    return {};
  }
  const counts = await apiClient<Record<string, number>>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/footprint-counts`,
    params: { tenantName, projectId },
  });
  return counts || {};
}

export function getSecretDiscoveryLocationCount(
  counts: Record<string, number> | undefined,
  secretName?: string,
): number {
  if (!counts || !secretName) return 0;
  if (counts[secretName] != null) return counts[secretName];
  const shortName = getShortSecretName(secretName);
  const match = Object.keys(counts).find((key) => getShortSecretName(key) === shortName);
  return match ? counts[match] : 0;
}

export class SecretDiscoveryScanConflictError extends Error {
  runningScanId?: string;

  constructor(message: string, runningScanId?: string) {
    super(message);
    this.name = "SecretDiscoveryScanConflictError";
    this.runningScanId = runningScanId;
  }
}

export async function startSecretDiscoveryScan(
  cloudProvider: string,
  tenantName: string,
  projectId: string,
  targetTypes?: SecretDiscoveryTargetType[],
): Promise<SecretDiscoveryScan> {
  try {
    return await apiClient<SecretDiscoveryScan>({
      method: "post",
      url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/scan`,
      data: { tenantName, projectId, targetTypes: targetTypes ?? [] },
    });
  } catch (error: any) {

    const body = error?.response?.data ?? error;
    if (body?.code === "SCAN_IN_PROGRESS") {
      throw new SecretDiscoveryScanConflictError(
        body?.message || "A scan is already in progress for this project",
        body?.runningScanId,
      );
    }
    const message =
      (typeof body === "string" && body) ||
      body?.message ||
      error?.message ||
      "Unable to start the scan";
    throw new Error(message);
  }
}

export const SCAN_TARGET_TYPE_LABELS: Record<SecretDiscoveryTargetType, string> = {
  COMPUTE_VM: "Scan virtual machines",
  STORAGE_BUCKET: "Scan buckets",
  DATABASE: "Scan databases",
  KUBERNETES_CLUSTER: "Scan Kubernetes clusters",
};

export const SCAN_TARGET_TYPE_ORDER: SecretDiscoveryTargetType[] = [
  "COMPUTE_VM",
  "STORAGE_BUCKET",
  "DATABASE",
];

export async function fetchSupportedScanTargetTypes(
  cloudProvider: string,
): Promise<SecretDiscoveryTargetType[]> {
  if (!isSupportedProvider(cloudProvider)) {
    return [];
  }
  const targetTypes = await apiClient<SecretDiscoveryTargetType[]>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/scan/target-types`,
  });
  return Array.isArray(targetTypes) ? targetTypes : [];
}

export type SecretDiscoveryDatabaseEngine = "POSTGRES" | "MYSQL" | "SQLSERVER";

export interface SecretDiscoveryDatabaseInstance {
  instanceName: string;
  engine?: SecretDiscoveryDatabaseEngine;
  engineVersion?: string;
  host?: string;
  region?: string;
  registered: boolean;
}

export interface SecretDiscoveryDatabaseCredential {
  id: string;
  instanceName: string;
  engine: SecretDiscoveryDatabaseEngine;
  databaseName: string;
  host?: string;
  port?: number;
  username: string;
  passwordSecretRef?: string;
  hasStoredPassword: boolean;
  requireSsl: boolean;
  enabled: boolean;
}

export interface SaveDatabaseCredentialInput {
  tenantName: string;
  projectId: string;
  instanceName: string;
  engine: SecretDiscoveryDatabaseEngine;
  databaseName: string;
  username: string;
  password?: string;
  passwordSecretRef?: string;
  host?: string;
  port?: number;
  requireSsl?: boolean;
}

export interface DatabaseCredentialTestResult {
  reachable: boolean;
  reason?: string;
  message?: string;
}

export async function fetchDatabaseInstances(
  cloudProvider?: string,
  tenantName?: string,
  projectId?: string,
): Promise<SecretDiscoveryDatabaseInstance[]> {
  if (!isSupportedProvider(cloudProvider) || !tenantName || !projectId) return [];
  const instances = await apiClient<SecretDiscoveryDatabaseInstance[]>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/database-instances`,
    params: { tenantName, projectId },
  });
  return Array.isArray(instances) ? instances : [];
}

export async function fetchDatabaseCredentials(
  cloudProvider?: string,
  tenantName?: string,
  projectId?: string,
): Promise<SecretDiscoveryDatabaseCredential[]> {
  if (!isSupportedProvider(cloudProvider) || !tenantName || !projectId) return [];
  const credentials = await apiClient<SecretDiscoveryDatabaseCredential[]>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/database-credentials`,
    params: { tenantName, projectId },
  });
  return Array.isArray(credentials) ? credentials : [];
}

export async function saveDatabaseCredential(
  cloudProvider: string,
  input: SaveDatabaseCredentialInput,
): Promise<SecretDiscoveryDatabaseCredential> {
  try {
    return await apiClient<SecretDiscoveryDatabaseCredential>({
      method: "post",
      url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/database-credentials`,
      data: input,
    });
  } catch (error: any) {
    const body = error?.response?.data ?? error;
    throw new Error(body?.message || error?.message || "Unable to save the credential");
  }
}

export async function deleteDatabaseCredential(credentialId: string): Promise<void> {
  await apiClient<void>({
    method: "delete",
    url: `${SECRET_DISCOVERY_BASE}/database-credentials/${credentialId}`,
  });
}

export async function testDatabaseCredential(
  credentialId: string,
  password?: string,
): Promise<DatabaseCredentialTestResult> {
  return apiClient<DatabaseCredentialTestResult>({
    method: "post",
    url: `${SECRET_DISCOVERY_BASE}/database-credentials/${credentialId}/test`,
    params: password ? { password } : undefined,
  });
}

export async function fetchSecretDiscoveryScanState(
  cloudProvider?: string,
  tenantName?: string,
  projectId?: string,
): Promise<SecretDiscoveryScanTypeState[]> {
  if (!isSupportedProvider(cloudProvider) || !tenantName || !projectId) return [];
  const state = await apiClient<SecretDiscoveryScanTypeState[]>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/scan/state`,
    params: { tenantName, projectId },
  });
  return Array.isArray(state) ? state : [];
}

export async function fetchSecretDiscoveryScanHistory(
  cloudProvider?: string,
  tenantName?: string,
  projectId?: string,
  limit = 10,
): Promise<SecretDiscoveryScan[]> {
  if (!isSupportedProvider(cloudProvider) || !tenantName || !projectId) return [];
  const scans = await apiClient<SecretDiscoveryScan[]>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/${normalizeProvider(cloudProvider)}/scans`,
    params: { tenantName, projectId, limit },
  });
  return Array.isArray(scans) ? scans : [];
}
